/**
 * LES SOURCES DE CONVERSATION DU CENTRE DE COMMUNICATION, et comment on s'y
 * branche (chantier `centre-communication`, `docs/socle/centre-communication.md`).
 *
 * Le centre ne connait pas Lia, ni les canaux, ni les messages directs : il
 * connait des SOURCES, chacune apportant un groupe du rail (Notifications,
 * Lia, Canaux, Messages directs, l'objet courant) et des conversations. Une
 * source se declare par ce contrat, et c'est tout : le rail la montre, la
 * rangee 3 porte ses gestes, la zone 5 rend sa conversation active.
 *
 * Les cinq sources sont BRANCHEES (vague finale du 05/10/2026) : Lia par son
 * port, Notifications par la boite interne, Canaux, Messages directs et Ici
 * par le port des conversations du socle (`sourcesConversations.ts`). Sans le
 * port de l'hote, une source redevient un emplacement qui dit pourquoi (G16) :
 * jamais de fausse donnee en production.
 */

import { SOURCE_NOTIFICATIONS } from '../notifications/source.js';

/** L'identifiant d'une source : stable, c'est lui que l'etat memorise. */
export type SourceId = 'notifications' | 'ici' | 'lia' | 'canaux' | 'directs';

/** Une conversation telle que le rail la liste : son titre, son groupe, ses non lus. */
export interface ConversationResume {
  id: string;
  source: SourceId;
  titre: string;
  /** Les non lus geles (G15) ; `0` : rien a signaler. */
  nonLus: number;
  /** Un point lime sans nombre : une reponse de Lia prete, par exemple. */
  pointLime?: boolean;
  /** La reference d'epinglage du socle : `lia:<sujetId>` ou `conversation:<id>`. */
  ref?: string;
  /** Epinglee PAR MOI (serveur) : elle vit dans le groupe « Epinglees », sous Notifications (R12). */
  epinglee?: boolean;
  /** Je peux la renommer (R4) : un sujet de Lia a moi, un canal que je gere. */
  renommable?: boolean;
  /** Je peux la supprimer, c'est-a-dire l'archiver (R4) : un sujet a moi, un canal que je gere, un direct (pour moi). */
  supprimable?: boolean;
  /** Un sujet de Lia qu'une autre personne partage avec moi : lecture seule, « partage par X ». */
  partagePar?: string;
  /** Le glyphe du rail : `#` pour un canal, un cadenas pour un canal prive, un rond de presence pour un direct, la tete du chat pour Lia. */
  glyphe?: 'hash' | 'cadenas' | 'presence' | 'lia' | 'fiche';
  /**
   * P22 du 06/10/2026 : la personne d'un message direct, montree par SA photo
   * dans la liste (Avatar, initiales en repli) a la place du visage generique.
   */
  personne?: { initiales: string; photoURL?: string | null };
  archivee?: boolean;
}

/** Ce qu'une source dit d'elle-meme. */
export interface SourceDeConversation {
  id: SourceId;
  /** Le nom du groupe dans le rail (« Lia », « Canaux »). */
  libelle: string;
  /** Le libelle du « + » du groupe (« Nouveau sujet »). Absent : pas de creation ici. */
  ajouterLabel?: string;
  /**
   * `prete` : la source a des conversations et un rendu. `a-venir` : un
   * emplacement, qui dit par `raison` ce qui le branchera.
   */
  etat: 'prete' | 'a-venir';
  /** La phrase de l'emplacement (G16 : un controle inerte porte sa raison). */
  raison?: string;
  /** Ou la source se pose dans le rail : avant les groupes (Notifications, Ici) ou parmi eux. */
  placement: 'tete' | 'groupe';
}

/** Les sources dans l'ORDRE du rail (maquette hub-a) : Notifications, Ici, Lia, Canaux, Messages directs. */
export const SOURCES_DU_CENTRE: readonly SourceDeConversation[] = [
  // Branchee par le chantier notifications (`../notifications/source.ts`) : la boite interne en zone 5.
  { ...SOURCE_NOTIFICATIONS },
  {
    id: 'ici',
    libelle: 'Ici',
    etat: 'prete',
    placement: 'tete',
    raison: 'Le suivi de l’objet ouvert (fiche, porte ou app) : l’hôte ne monte pas le port des conversations.',
  },
  {
    id: 'lia',
    libelle: 'Lia',
    ajouterLabel: 'Nouveau sujet avec Lia',
    etat: 'prete',
    placement: 'groupe',
  },
  {
    id: 'canaux',
    libelle: 'Canaux',
    ajouterLabel: 'Nouveau canal',
    etat: 'prete',
    placement: 'groupe',
    raison: 'Les canaux de l’organisation : l’hôte ne monte pas le port des conversations.',
  },
  {
    id: 'directs',
    libelle: 'Messages directs',
    ajouterLabel: 'Nouveau message direct',
    etat: 'prete',
    placement: 'groupe',
    raison: 'Les messages directs : l’hôte ne monte pas le port des conversations.',
  },
];

export function sourceDuCentre(id: SourceId): SourceDeConversation {
  const source = SOURCES_DU_CENTRE.find((candidate) => candidate.id === id);
  if (source === undefined) throw new Error(`Source inconnue du centre : ${id}`);
  return source;
}

/** Les sources qui forment un GROUPE du rail (avec en-tete et « + »), dans l'ordre. */
export function groupesDuRail(): readonly SourceDeConversation[] {
  return SOURCES_DU_CENTRE.filter((source) => source.placement === 'groupe');
}

/**
 * Les non lus a afficher sur l'icone d'une source dans le rail compact :
 * la somme GELEE des non lus de ses conversations, et le point lime si l'une
 * d'elles en porte un sans nombre.
 */
export function badgeDeSource(conversations: readonly ConversationResume[], source: SourceId): { nombre: number; point: boolean } {
  const siennes = conversations.filter((conversation) => conversation.source === source && !conversation.archivee);
  return {
    nombre: siennes.reduce((total, conversation) => total + conversation.nonLus, 0),
    point: siennes.some((conversation) => conversation.pointLime === true),
  };
}
